import { isNull, isNumber } from 'lodash';
import { ICollaboratorInfo, ICollaboratorsData, IMemberInfo } from '../types';
import { getRandomColor, parseWsKey } from '../utils';

export class Member {
    constructor(private info: ICollaboratorInfo) {}

    setReady(ready: boolean) {
        this.info.isReady = ready;
    }

    setSaving(saving: boolean) {
        this.info.isSaving = saving;
    }

    setInfo(info: ICollaboratorInfo) {
        this.info = info;
    }

    getInfo() {
        return this.info;
    }

    /** 是否有选中数据 */
    hasPickInfo() {
        return !!(this.info.activeId || this.info.pickId);
    }

    /** 设置选中数据 */
    setPickInfo(activeId: string, pickId: string, cover = true) {
        const member = this.info;
        member.activeId = cover ? activeId : isNull(member.activeId) ? activeId : member.activeId;
        member.pickId = cover ? pickId : isNull(member.pickId) ? pickId : member.pickId;
    }

    /** 获取选中数据 */
    getPickInfo() {
        return {
            activeId: this.info.activeId || '',
            pickId: this.info.pickId || '',
        };
    }

    /** 设置光标数据 */
    setCursorInfo(originIndex: number, x: number, y: number, cover = true) {
        const member = this.info;
        member.originIndex = cover ? originIndex : isNull(member.originIndex) ? originIndex : member.originIndex;
        member.x = cover ? x : isNull(member.x) ? x : member.x;
        member.y = cover ? y : isNull(member.y) ? y : member.y;
    }

    /** 设置最后活跃时间 */
    setLastActiveTime(lastActiveTime: number) {
        this.info.lastActiveTime = lastActiveTime;
    }
}

class ColorManager {
    /** 协作成员颜色列表 */
    // private colors: string[] = ['#FF2525', '#FF6200', '#FCCA00', '#27B148', '#00C7FF', '#4D9BF1', '#A411D1'];

    /** 获取协作成员颜色 */
    getColor() {
        return getRandomColor();
        // return this.colors.shift() || getRandomColor();
    }

    /** 归还协作成员颜色 */
    returnColor(color: string) {
        // this.colors.push(color);
        console.log('returnColor', color);
    }
}

export class Collaborators {
    private currentWsKey: number;
    private membersMap: Map<number, Member>;
    private membersList: Member[];
    private colorManager: ColorManager;
    private serverTime: number;

    constructor() {
        this.currentWsKey = 0;
        this.serverTime = 0;
        this.membersMap = new Map<number, Member>();
        this.membersList = [];
        this.colorManager = new ColorManager();
    }

    /** 是否是最后活跃成员 */
    isLastActiveMember() {
        return (
            this.membersList
                .sort((a, b) => {
                    // 先按 lastActiveTime 升序排列
                    const timeDiff = b.getInfo().lastActiveTime - a.getInfo().lastActiveTime;
                    if (timeDiff !== 0) {
                        return timeDiff;
                    }
                    // 如果 lastActiveTime 相同，再按 wsKey 降序排列
                    return a.getInfo().wsKey - b.getInfo().wsKey;
                })?.[0]
                .getInfo().wsKey === this.getWsKey()
        );
    }

    /** 获取当前协作成员的 wskey */
    getWsKey() {
        return this.currentWsKey;
    }

    /** 获取服务端时间 */
    getServerTime() {
        return this.serverTime;
    }

    /** 设置当前协作成员的 wskey */
    setWsKey(key: string) {
        this.currentWsKey = parseWsKey(key).wsKey;
        this.serverTime = Math.floor(this.currentWsKey / 1000);
    }

    /** 是否是主协作成员 */
    isMaster() {
        return !!this.getMember(this.getWsKey())?.getInfo().isMaster;
    }

    /** 协作成员数量 */
    getLength() {
        return this.membersList.length;
    }

    /** 协作成员列表 */
    getMembers() {
        return this.membersList;
    }

    /** 获取除当前以外的其他协作成员列表 */
    getOtherMembers() {
        return this.membersList.filter((m) => m.getInfo().wsKey !== this.getWsKey());
    }

    /** 获取指定成员 */
    getMember(wsKey?: number) {
        if (!isNumber(wsKey)) {
            wsKey = this.getWsKey();
        }
        return this.membersMap.get(wsKey);
    }

    /** 获取有选中数据的协作成员 */
    getHasPickInfoMembers() {
        return this.membersList.filter((item) => item.hasPickInfo());
    }

    /** 销毁协作成员 */
    dispose() {
        this.currentWsKey = 0;
        this.membersMap.clear();
        this.membersList.length = 0;
    }

    /** 设置在线成员数据 */
    setOnlineMembersData(data: ICollaboratorsData, from?: string) {
        if (data && data.members && data.ws_member_keys) {
            const sortedData = data.ws_member_keys.map(parseWsKey).sort((a, b) => a.wsKey - b.wsKey);
            const userInfo: Record<number, IMemberInfo> = {};
            const newMembersMap: Map<number, Member> = new Map();
            const masterWsKey = sortedData[0].wsKey;

            data.members.forEach((member) => {
                userInfo[member.id] = member;
            });
            sortedData.forEach((item) => {
                const { wsKey, uid } = item;
                const member = this.membersMap.get(wsKey);
                const oldInfo = member?.getInfo() || ({} as ICollaboratorInfo);
                const newInfo: ICollaboratorInfo = {
                    wsKey,
                    ...userInfo[uid],
                    color: oldInfo.color || this.colorManager.getColor(),
                    activeId: oldInfo.activeId || null,
                    pickId: oldInfo.pickId || null,
                    originIndex: oldInfo.originIndex || null,
                    x: oldInfo.x || null,
                    y: oldInfo.y || null,
                    isReady: oldInfo.isReady || false,
                    isSaving: oldInfo.isSaving || false,
                    isCurrent: wsKey === this.getWsKey(),
                    isMaster: wsKey === masterWsKey,
                    lastActiveTime: oldInfo.lastActiveTime || 0,
                };
                const newMember = member || new Member(newInfo);
                newMember.setInfo(newInfo);
                newMembersMap.set(wsKey, newMember);
                this.membersMap.delete(wsKey);
            });
            // 归还离线成员的颜色
            for (const [_, member] of this.membersMap.entries()) {
                this.colorManager.returnColor(member.getInfo().color);
            }
            // 设置新的协作成员数据
            this.membersMap = newMembersMap;
            this.membersList = sortedData.map((item) => {
                return this.membersMap.get(item.wsKey)!;
            });
            console.log('ws Collaborators members', from, this.membersList);
            return true;
        }
        return false;
    }
}
